import { useState } from "react";
import {
  BookOpen,
  Plus,
  Search,
  MoreHorizontal,
  Edit,
  Trash2,
  Eye,
  Tag,
  Lock,
  Unlock,
  Filter,
  Loader2,
} from "lucide-react";
import { AdminLayout } from "@jambushrushti/components/admin/AdminLayout";
import {
  AdminViewToggle,
  useAdminViewMode,
} from "@jambushrushti/components/admin/AdminViewToggle";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@jambushrushti/components/ui/card";
import { Button } from "@jambushrushti/components/ui/button";
import { Input } from "@jambushrushti/components/ui/input";
import { Badge } from "@jambushrushti/components/ui/badge";
import { Textarea } from "@jambushrushti/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@jambushrushti/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@jambushrushti/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@jambushrushti/components/ui/dialog";
import { Label } from "@jambushrushti/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@jambushrushti/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@jambushrushti/components/ui/tabs";
import { Switch } from "@jambushrushti/components/ui/switch";
import {
  useGuruvaniList,
  useCreateGuruvani,
  useUpdateGuruvani,
  useDeleteGuruvani,
  GuruvaniInput,
} from "@jambushrushti/hooks/useGuruvani";

export default function GuruvaniManagement() {
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useAdminViewMode();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newGuruvani, setNewGuruvani] = useState<GuruvaniInput>({
    title: "",
    content: "",
    category: "",
    source: "",
    tags: "",
    is_published: false,
    is_restricted: false,
  });

  const { data: guruvaniList, isLoading, error } = useGuruvaniList(true);
  const createGuruvani = useCreateGuruvani();
  const updateGuruvani = useUpdateGuruvani();
  const deleteGuruvani = useDeleteGuruvani();

  const filteredGuruvani =
    guruvaniList?.filter(
      item =>
        item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.category?.toLowerCase().includes(searchQuery.toLowerCase())
    ) || [];

  const availableTags = Array.from(
    new Set(
      (guruvaniList || []).flatMap(item =>
        item.tags
          .split(",")
          .map(tag => tag.trim())
          .filter(Boolean)
      )
    )
  );

  const handleCreate = async () => {
    if (!newGuruvani.title) {
      return;
    }

    if (editingId) {
      await updateGuruvani.mutateAsync({ id: editingId, ...newGuruvani });
    } else {
      await createGuruvani.mutateAsync(newGuruvani);
    }
    setIsAddDialogOpen(false);
    setNewGuruvani({
      title: "",
      content: "",
      category: "",
      source: "",
      tags: "",
      is_published: false,
      is_restricted: false,
    });
    setEditingId(null);
  };

  const handleEdit = (item: (typeof filteredGuruvani)[number]) => {
    setEditingId(item.id);
    setNewGuruvani({
      title: item.title,
      content: item.content || "",
      category: item.category || "",
      source: item.source || "",
      tags: item.tags || "",
      is_published: item.is_published,
      is_restricted: item.is_restricted,
    });
    setIsAddDialogOpen(true);
  };

  const handleTogglePublish = async (id: string, currentStatus: boolean) => {
    await updateGuruvani.mutateAsync({ id, is_published: !currentStatus });
  };

  const handleToggleRestricted = async (id: string, currentStatus: boolean) => {
    await updateGuruvani.mutateAsync({ id, is_restricted: !currentStatus });
  };

  const handleDelete = async (id: string) => {
    if (confirm("Are you sure you want to delete this Guruvani?")) {
      await deleteGuruvani.mutateAsync(id);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="font-display text-2xl font-bold text-foreground">
              Guruvani Management
            </h1>
            <p className="mt-1 text-muted-foreground">
              Manage sacred teachings and content
            </p>
          </div>
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-xl bg-gradient-to-r from-primary to-gold hover:opacity-90">
                <Plus className="mr-2 h-4 w-4" />
                Add Guruvani
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl rounded-2xl">
              <DialogHeader>
                <DialogTitle>
                  {editingId ? "Edit Guruvani" : "Add New Guruvani"}
                </DialogTitle>
                <DialogDescription>
                  Add sacred teaching content
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Title *</Label>
                    <Input
                      placeholder="Enter title"
                      className="rounded-xl"
                      value={newGuruvani.title}
                      onChange={e =>
                        setNewGuruvani({
                          ...newGuruvani,
                          title: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select
                      value={newGuruvani.category || ""}
                      onValueChange={v =>
                        setNewGuruvani({ ...newGuruvani, category: v })
                      }
                    >
                      <SelectTrigger className="rounded-xl">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sutra">Sutra</SelectItem>
                        <SelectItem value="discourse">Discourse</SelectItem>
                        <SelectItem value="audio">Audio</SelectItem>
                        <SelectItem value="document">Document</SelectItem>
                        <SelectItem value="video">Video</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Source</Label>
                  <Input
                    placeholder="Source reference"
                    className="rounded-xl"
                    value={newGuruvani.source || ""}
                    onChange={e =>
                      setNewGuruvani({ ...newGuruvani, source: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tags</Label>
                  <Input
                    placeholder="knowledge, philosophy"
                    className="rounded-xl"
                    value={newGuruvani.tags || ""}
                    onChange={e =>
                      setNewGuruvani({ ...newGuruvani, tags: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>Content</Label>
                  <Textarea
                    placeholder="Full teaching content..."
                    className="rounded-xl"
                    rows={4}
                    value={newGuruvani.content || ""}
                    onChange={e =>
                      setNewGuruvani({
                        ...newGuruvani,
                        content: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={newGuruvani.is_published}
                      onCheckedChange={checked =>
                        setNewGuruvani({
                          ...newGuruvani,
                          is_published: checked,
                        })
                      }
                    />
                    <Label>Publish immediately</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={newGuruvani.is_restricted}
                      onCheckedChange={checked =>
                        setNewGuruvani({
                          ...newGuruvani,
                          is_restricted: checked,
                        })
                      }
                    />
                    <Label>Restricted access</Label>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setIsAddDialogOpen(false)}
                  className="rounded-xl"
                >
                  Cancel
                </Button>
                <Button
                  className="rounded-xl bg-primary"
                  onClick={handleCreate}
                  disabled={
                    createGuruvani.isPending ||
                    updateGuruvani.isPending ||
                    !newGuruvani.title
                  }
                >
                  {createGuruvani.isPending || updateGuruvani.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Adding...
                    </>
                  ) : editingId ? (
                    "Save Changes"
                  ) : (
                    "Add Guruvani"
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="content" className="space-y-6">
          <TabsList className="rounded-xl bg-muted/50 p-1">
            <TabsTrigger
              value="content"
              className="rounded-lg data-[state=active]:bg-background"
            >
              <BookOpen className="mr-2 h-4 w-4" />
              Content
            </TabsTrigger>
            <TabsTrigger
              value="tags"
              className="rounded-lg data-[state=active]:bg-background"
            >
              <Tag className="mr-2 h-4 w-4" />
              Tags
            </TabsTrigger>
          </TabsList>

          <TabsContent value="content" className="space-y-4">
            {/* Search & Filter */}
            <Card className="shadow-soft rounded-2xl border-0">
              <CardContent className="p-4">
                <div className="flex flex-col gap-4 sm:flex-row">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder="Search Guruvani..."
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      className="rounded-xl pl-10"
                    />
                  </div>
                  <Select>
                    <SelectTrigger className="w-[180px] rounded-xl">
                      <Filter className="mr-2 h-4 w-4" />
                      <SelectValue placeholder="Filter by type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="sutra">Sutra</SelectItem>
                      <SelectItem value="discourse">Discourse</SelectItem>
                      <SelectItem value="audio">Audio</SelectItem>
                      <SelectItem value="document">Document</SelectItem>
                    </SelectContent>
                  </Select>
                  <AdminViewToggle value={viewMode} onChange={setViewMode} />
                </div>
              </CardContent>
            </Card>

            {/* Content Table */}
            <Card className="shadow-soft rounded-2xl border-0">
              <CardContent className="p-0">
                {isLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                ) : error ? (
                  <div className="py-12 text-center text-destructive">
                    Error loading data. Please try again.
                  </div>
                ) : filteredGuruvani.length === 0 ? (
                  <div className="py-12 text-center text-muted-foreground">
                    No Guruvani content found. Add your first one!
                  </div>
                ) : viewMode === "table" ? (
                  <Table>
                    <TableHeader>
                      <TableRow className="hover:bg-transparent">
                        <TableHead>Title</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Access</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredGuruvani.map(item => (
                        <TableRow key={item.id} className="hover:bg-muted/30">
                          <TableCell className="font-medium">
                            {item.title}
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant="outline"
                              className="bg-primary/10 text-primary"
                            >
                              {item.category || "Uncategorized"}
                            </Badge>
                          </TableCell>
                          <TableCell>{item.source || "-"}</TableCell>
                          <TableCell>
                            <Badge
                              variant="outline"
                              className={
                                item.is_published
                                  ? "bg-sage/10 text-sage cursor-pointer"
                                  : "cursor-pointer bg-muted text-muted-foreground"
                              }
                              onClick={() =>
                                handleTogglePublish(item.id, item.is_published)
                              }
                            >
                              {item.is_published ? "Published" : "Draft"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <button
                              onClick={() =>
                                handleToggleRestricted(
                                  item.id,
                                  item.is_restricted
                                )
                              }
                            >
                              {item.is_restricted ? (
                                <Lock className="h-4 w-4 text-destructive" />
                              ) : (
                                <Unlock className="text-sage h-4 w-4" />
                              )}
                            </button>
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="rounded-lg"
                                >
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent
                                align="end"
                                className="rounded-xl"
                              >
                                <DropdownMenuItem>
                                  <Eye className="mr-2 h-4 w-4" />
                                  View
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handleEdit(item)}
                                >
                                  <Edit className="mr-2 h-4 w-4" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-destructive"
                                  onClick={() => handleDelete(item.id)}
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="grid gap-4 p-4 md:grid-cols-2 xl:grid-cols-3">
                    {filteredGuruvani.map(item => (
                      <Card key={item.id} className="border shadow-sm">
                        <CardContent className="space-y-3 p-4">
                          <div className="flex items-start justify-between gap-3">
                            <h3 className="line-clamp-2 font-semibold">
                              {item.title}
                            </h3>
                            <Badge
                              variant={
                                item.is_published ? "default" : "secondary"
                              }
                            >
                              {item.is_published ? "Published" : "Draft"}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {item.source || "No source"}
                          </p>
                          <Badge variant="outline">
                            {item.category || "Uncategorized"}
                          </Badge>
                          <div className="flex gap-2 border-t pt-3">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEdit(item)}
                            >
                              <Edit className="mr-1 h-3.5 w-3.5" /> Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-destructive"
                              onClick={() => handleDelete(item.id)}
                            >
                              <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="tags" className="space-y-4">
            <Card className="shadow-soft rounded-2xl border-0">
              <CardHeader>
                <CardTitle className="text-lg">Manage Tags</CardTitle>
                <CardDescription>
                  Organize Guruvani content with tags
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {availableTags.map(tag => (
                    <Badge
                      key={tag}
                      variant="secondary"
                      className="px-4 py-2 text-sm"
                    >
                      {tag}
                      <button className="ml-2 text-muted-foreground hover:text-destructive">
                        ×
                      </button>
                    </Badge>
                  ))}
                  <Button variant="outline" size="sm" className="rounded-full">
                    <Plus className="mr-1 h-4 w-4" />
                    Add Tag
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
